import CategoryButton from '@/components/elements/CategoryButton';
import ContainerLayout from '@/components/layouts/ContainerLayout';
import FakeSearchForm, {
  FakeSearchFormRef,
  OnLocationUpdate,
} from '@/features/searchV2/modules/FakeSearchForm/FakeSearchForm';
import { classnames } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { DEALS_KEYWORD_SEARCH_SHORTCUTS } from './DealsLanding.constants';

interface HeroSectionProps {
  showSearchForm?: boolean;
  searchFormContainerRef?: React.RefObject<HTMLDivElement>;
  searchFormRef: React.RefObject<FakeSearchFormRef>;
  onCategoryClick: (keyword: string) => void;
  onLocationUpdate?: OnLocationUpdate;
}

export const HeroSection = ({
  searchFormRef,
  searchFormContainerRef,
  showSearchForm,
  onCategoryClick,
  onLocationUpdate,
}: HeroSectionProps) => {
  const { isMobileView } = useMobileView();
  return (
    <section aria-labelledby="hero-heading">
      {/* Hero content */}
      <ContainerLayout innerClassName="relative flex flex-col-reverse xl:flex-row xl:items-center">
        <div
          className={classnames(
            'relative z-10',
            // -> tablet styles
            'pb-xl space-y-xl',
            // tablet -> desktop styles
            'md:space-y-2xl md:pb-cv-margin',
            // -> Desktop styles
            'xl:space-y-3xl xl:my-3xl xl:py-3xl xl:min-h-[350px] xl:w-[calc(100%-480px)]',
          )}>
          {/* Heading */}
          <h1 id="hero-heading" className={classnames('text-center font-bold xl:text-left', 'text-h-2xl')}>
            <span className="text-fg_highlight text-h-2xl">Deals</span>{' '}
            <span className="text-fg_primary">
              {_t('features.deals.pages.DealsLanding.components.HeroSection.title')}
            </span>
          </h1>

          {/* Search form */}
          <div className="relative z-10">
            <div ref={searchFormContainerRef}></div>
            {showSearchForm ? (
              <FakeSearchForm ref={searchFormRef} type="deals" onLocationUpdate={onLocationUpdate} />
            ) : (
              <div className="h-[52px]" />
            )}
          </div>

          {/* Category shortcuts */}
          <div className="gap-sm flex flex-wrap justify-center xl:justify-start">
            {(isMobileView ? DEALS_KEYWORD_SEARCH_SHORTCUTS.slice(0, 5) : DEALS_KEYWORD_SEARCH_SHORTCUTS).map(
              (category) => (
                <CategoryButton
                  key={category.slug}
                  label={category.value}
                  icon={category.icon}
                  onClick={() => onCategoryClick(category.slug)}
                />
              ),
            )}
          </div>
        </div>
        <div className={classnames('relative xl:ml-auto xl:h-[348px]')} aria-hidden="true">
          <picture>
            <source srcSet="/images/deals-hero-desktop.webp" media="(min-width: 1140px)" type="image/webp" />
            <source srcSet="/images/deals-hero-desktop.png" media="(min-width: 1140px)" />
            <source srcSet="/images/deals-hero-tablet.avif" media="(min-width: 576px)" type="image/avif" />
            <source srcSet="/images/deals-hero-tablet.webp" media="(min-width: 576px)" type="image/webp" />
            <source srcSet="/images/deals-hero-tablet.png" media="(min-width: 576px)" />
            <source srcSet="/images/deals-hero-mobile1.avif" type="image/avif" />
            <source srcSet="/images/deals-hero-mobile1.webp" type="image/webp" />
            <img
              src="/images/deals-hero-mobile1.png"
              alt=""
              className="h-full w-full object-cover object-bottom"
              loading="eager"
              fetchPriority="high"
            />
          </picture>
        </div>
      </ContainerLayout>
    </section>
  );
};
